<script setup lang="ts">
import { Deferred, Head, Link, router, useForm } from "@inertiajs/vue3"
import { FileText, SlidersHorizontal, X } from "lucide-vue-next"
import { computed, ref, watch } from "vue"


import AppDataTable from "@/components/AppDataTable.vue"
import type { TableColumn } from "@/components/AppDataTable.vue"
import AppPagination from "@/components/AppPagination.vue"
import DobInput from "@/components/DobInput.vue"
import HeadingSmall from "@/components/HeadingSmall.vue"
import InputError from "@/components/InputError.vue"
import PhoneInput from "@/components/PhoneInput.vue"
import RelativeTime from "@/components/RelativeTime.vue"
import StatusBadge from "@/components/applications/StatusBadge.vue"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import AppLayout from "@/layouts/AppLayout.vue"
import CustomerLayout from "@/layouts/customers/Layout.vue"
import { APPLICATION_STATUSES } from "@/lib/constants"
import { formatAmount, formatLabel } from "@/lib/utils"
import {
  customerPath,
  customersPath,
  editCustomerPath,
  editLoanApplicationPath,
} from "@/routes"
import { type BreadcrumbItem } from "@/types"

interface Customer {
  id: number
  first_name: string
  last_name: string
  middle_name: string | null
  nickname: string | null
  gender: string
  date_of_birth: string
  email: string
  phone: string
  on_hold: boolean
}

interface Application {
  id: string
  status: string
  status_at: string | null
  rejection_reason: string | null
  loan_type: string
  purpose: string
  amount: number
  created_at: string
  completed_in_full: boolean
}

interface PaginationMeta {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface AppFilters {
  status?: string | null
  loan_type?: string | null
}

const props = defineProps<{
  customer: Customer
  applications?: { records: Application[]; pagination: PaginationMeta; filters: AppFilters } | null
  genders: string[]
  loan_types: string[]
}>()



function formatGender(g: string) {
  return g.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Customers", href: customersPath() },
  {
    title: `${props.customer.first_name} ${props.customer.last_name}`,
    href: editCustomerPath(props.customer.id),
  },
]

const urlSection = new URLSearchParams(window.location.search).get("section")
const validSections = ["details", "applications"] as const
type Section = (typeof validSections)[number]
const activeSection = ref<Section>(
  validSections.includes(urlSection as Section) ? (urlSection as Section) : "details",
)

const form = useForm({
  first_name: props.customer.first_name,
  last_name: props.customer.last_name,
  middle_name: props.customer.middle_name ?? "",
  nickname: props.customer.nickname ?? "",
  gender: props.customer.gender,
  date_of_birth: props.customer.date_of_birth,
  email: props.customer.email,
  phone: props.customer.phone,
  on_hold: props.customer.on_hold,
})

function submit() {
  form.patch(customerPath(props.customer.id), {
    preserveScroll: true,
  })
}



// ── Applications filters ───────────────────────────────────────────────────
const ALL = "__all__"
const urlParams = new URLSearchParams(window.location.search)

const appStatusFilter = ref(urlParams.get("status") ?? ALL)
const appLoanTypeFilter = ref(urlParams.get("loan_type") ?? ALL)

const hasAppFilters = computed(
  () => appStatusFilter.value !== ALL || appLoanTypeFilter.value !== ALL,
)

const appFilterParams = computed(() => ({
  section: "applications",
  status: appStatusFilter.value !== ALL ? appStatusFilter.value : undefined,
  loan_type: appLoanTypeFilter.value !== ALL ? appLoanTypeFilter.value : undefined,
}))

function applyAppFilters() {
  const params = Object.fromEntries(
    Object.entries(appFilterParams.value).filter(([, v]) => v != null),
  )
  router.get(editCustomerPath(props.customer.id), params, {
    preserveState: true,
    preserveScroll: true,
    replace: true,
  })
}

watch([appStatusFilter, appLoanTypeFilter], applyAppFilters)

function clearAppFilters() {
  appStatusFilter.value = ALL
  appLoanTypeFilter.value = ALL
}

const appTableColumns: TableColumn[] = [
  { key: "purpose", label: "Purpose" },
  { key: "amount", label: "Amount" },
  { key: "status", label: "Status" },
  { key: "complete", label: "Complete" },
  { key: "date", label: "Date" },
  { key: "actions", label: "", align: "right" },
]
</script>

<template>
  <Head :title="`Edit ${customer.first_name} ${customer.last_name}`" />

  <AppLayout :breadcrumbs="breadcrumbs">
    <CustomerLayout
      v-model:active-section="activeSection"
      :customer-id="customer.id"
      :customer-name="`${customer.first_name} ${customer.last_name}`"
    >
      <!-- ── Details section ───────────────────────────────────────────── -->
      <div v-show="activeSection === 'details'" class="space-y-6">
        <HeadingSmall
          title="Customer details"
          description="Update the customer's personal information"
        />

        <form class="space-y-4" @submit.prevent="submit">
          <div class="grid grid-cols-2 gap-4">
            <div class="grid gap-2">
              <Label for="first_name">First Name</Label>
              <Input id="first_name" v-model="form.first_name" placeholder="John" required />
              <InputError :messages="form.errors.first_name" />
            </div>
            <div class="grid gap-2">
              <Label for="last_name">Last Name</Label>
              <Input id="last_name" v-model="form.last_name" placeholder="Smith" required />
              <InputError :messages="form.errors.last_name" />
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div class="grid gap-2">
              <Label for="middle_name">Middle Name</Label>
              <Input id="middle_name" v-model="form.middle_name" placeholder="Optional" />
            </div>
            <div class="grid gap-2">
              <Label for="nickname">Preferred Name</Label>
              <Input id="nickname" v-model="form.nickname" placeholder="Optional" />
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div class="grid gap-2">
              <Label for="gender">Gender</Label>
              <Select v-model="form.gender">
                <SelectTrigger class="w-full" id="gender">
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="g in props.genders" :key="g" :value="g">
                    {{ formatGender(g) }}
                  </SelectItem>
                </SelectContent>
              </Select>
              <InputError :messages="form.errors.gender" />
            </div>
            <div class="grid gap-2">
              <Label for="date_of_birth">Date of Birth</Label>
              <DobInput id="date_of_birth" v-model="form.date_of_birth" />
              <InputError :messages="form.errors.date_of_birth" />
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div class="grid gap-2">
              <Label for="email">Email</Label>
              <Input id="email" v-model="form.email" type="email" placeholder="john@example.com" required />
              <InputError :messages="form.errors.email" />
            </div>
            <div class="grid gap-2">
              <Label for="phone">Phone</Label>
              <PhoneInput id="phone" v-model="form.phone" />
              <InputError :messages="form.errors.phone" />
            </div>
          </div>

          <div class="flex items-center gap-3">
            <Switch id="on_hold" :checked="form.on_hold" @update:checked="form.on_hold = $event" />
            <Label for="on_hold">On Hold</Label>
          </div>

          <div class="flex items-center gap-4 pt-2">
            <Button type="submit" :disabled="form.processing">Save Changes</Button>
            <Transition
              enter-active-class="transition ease-in-out"
              enter-from-class="opacity-0"
              leave-active-class="transition ease-in-out"
              leave-to-class="opacity-0"
            >
              <p v-show="form.recentlySuccessful" class="text-muted-foreground text-sm">
                Saved.
              </p>
            </Transition>
          </div>
        </form>
      </div>

      <!-- ── Applications section ──────────────────────────────────────── -->
      <div v-show="activeSection === 'applications'" class="space-y-4">
        <HeadingSmall
          title="Applications"
          description="All loan applications for this customer"
        />

        <!-- Filters -->
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Select v-model="appStatusFilter">
            <SelectTrigger class="w-full sm:w-40">
              <SelectValue placeholder="Any status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem :value="ALL">Any status</SelectItem>
              <SelectItem v-for="s in APPLICATION_STATUSES" :key="s" :value="s">{{ formatLabel(s) }}</SelectItem>
            </SelectContent>
          </Select>
          <Select v-model="appLoanTypeFilter">
            <SelectTrigger class="w-full sm:w-40">
              <SelectValue placeholder="Any type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem :value="ALL">Any type</SelectItem>
              <SelectItem v-for="t in props.loan_types" :key="t" :value="t">{{ formatLabel(t) }}</SelectItem>
            </SelectContent>
          </Select>
          <Button v-if="hasAppFilters" variant="ghost" size="sm" class="text-muted-foreground hover:text-foreground shrink-0" @click="clearAppFilters">
            <X class="size-4" />
            Clear
          </Button>
        </div>

        <Deferred data="applications">
          <template #fallback>
            <div class="space-y-2">
              <Skeleton class="h-10 w-full" />
              <Skeleton class="h-10 w-full" />
              <Skeleton class="h-10 w-full" />
            </div>
          </template>

          <div v-if="!applications || applications.records.length === 0" class="flex flex-col items-center gap-3 py-10 text-center">
            <FileText class="text-muted-foreground/40 size-10" />
            <p class="text-muted-foreground text-sm">
              {{ hasAppFilters ? "No applications match your filters" : "No applications yet" }}
            </p>
            <Button v-if="hasAppFilters" variant="outline" size="sm" @click="clearAppFilters">
              <X class="size-4" />
              Clear filters
            </Button>
          </div>

          <div v-else class="rounded-lg border">
            <div class="border-border flex items-center justify-between border-b px-4 py-2">
              <span class="text-muted-foreground text-xs">
                {{ applications.pagination.total_count }} application{{ applications.pagination.total_count !== 1 ? "s" : "" }}
                {{ hasAppFilters ? "found" : "total" }}
              </span>
              <SlidersHorizontal class="text-muted-foreground size-3.5" />
            </div>
            <AppDataTable :columns="appTableColumns" compact>
              <tr
                v-for="app in applications.records"
                :key="app.id"
                class="border-border hover:bg-muted/50 border-b transition-colors last:border-0"
              >
                <td class="px-4 py-3 font-medium">{{ formatLabel(app.purpose) }}</td>
                <td class="text-muted-foreground px-4 py-3">{{ formatAmount(app.amount) }}</td>
                <td class="px-4 py-3"><StatusBadge :status="app.status" :status-at="app.status_at" :rejection-reason="app.rejection_reason" /></td>
                <td class="px-4 py-3">
                  <Badge :variant="app.completed_in_full ? 'default' : 'secondary'">
                    {{ app.completed_in_full ? "Yes" : "No" }}
                  </Badge>
                </td>
                <td class="text-muted-foreground px-4 py-3"><RelativeTime :date="app.created_at" /></td>
                <td class="px-4 py-3 text-right">
                  <Link :href="editLoanApplicationPath(app.id)" class="text-primary text-xs hover:underline">Open →</Link>
                </td>
              </tr>
            </AppDataTable>
            <AppPagination
              :current-page="applications.pagination.current_page"
              :total-pages="applications.pagination.total_pages"
              :total-count="applications.pagination.total_count"
              :per-page="applications.pagination.per_page"
              :base-url="editCustomerPath(customer.id)"
              :extra-params="appFilterParams"
            />
          </div>
        </Deferred>
      </div>
    </CustomerLayout>
  </AppLayout>
</template>
